import { Controller } from "@hotwired/stimulus"

// Builds a Mittellohn out of tariff wage groups and their Lohnstunden, then offers it as
// the starting value for the Mittellohn field of the rate being edited.
//
// No <form> element anywhere in here: this panel renders inside the Lohngruppen form, and
// a nested form would be dropped by the HTML parser along with whatever hidden fields it
// carries. That is also why Enter in a crew field is bound to #calculate — otherwise it
// would implicitly submit the surrounding Lohngruppen form.
export default class extends Controller {
  static targets = ["row", "rowTemplate", "rows", "result", "resultValue", "error", "apply"]
  static values = {
    url: String,
    rates: String,
    errorMessage: String,
    staleMessage: String,
    noTargetMessage: String,
  }

  connect() {
    this.averageWage = null
    this.calculatedFor = null
  }

  addRow(event) {
    event.preventDefault()
    const row = this.rowTemplateTarget.content.cloneNode(true)
    this.rowsTarget.appendChild(row)
    this.invalidate()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest("[data-tariff-average-wage-target='row']")
    if (row) row.remove()
    this.invalidate()
  }

  // Any edit to the crew makes a previous result meaningless — drop it rather than leave
  // an "Übernehmen" button standing that would write a number nobody can see any more.
  invalidate() {
    this.averageWage = null
    this.calculatedFor = null
    this.errorTarget.hidden = true
    this.hideResult()
  }

  async calculate(event) {
    event.preventDefault()

    const rows = this.crewRows()

    if (rows.length === 0) {
      this.showError(this.errorMessageValue)
      return
    }

    // The Stichtag is the validity date of the Satz the value will land in, not today:
    // a row prefilled for the next Tarifstufe has to be priced with that step.
    const targetRow = this.targetRow()
    const validFrom = this.validFromOf(targetRow)

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ rows: rows, date: validFrom }),
      })

      const payload = await response.json()

      if (!response.ok) {
        this.showError(payload.error || this.errorMessageValue)
        return
      }

      this.averageWage = payload.average_wage
      this.calculatedFor = validFrom
      this.showResult(payload)
    } catch (error) {
      console.error("Mittellohn preview failed:", error)
      this.showError(this.errorMessageValue)
    }
  }

  apply(event) {
    event.preventDefault()
    if (this.averageWage === null) return

    const row = this.targetRow()
    if (!row) {
      this.showError(this.noTargetMessageValue)
      return
    }

    // Adding a Satz after calculating moves both the target and the Stichtag. Refusing is
    // the honest answer: silently writing a value derived from a different tariff period
    // is exactly the mistake the copied-not-linked design avoids everywhere else.
    if (this.validFromOf(row) !== this.calculatedFor) {
      this.showError(this.staleMessageValue)
      return
    }

    const field = row.querySelector("[data-role='average-wage']")
    if (!field) {
      this.showError(this.noTargetMessageValue)
      return
    }

    field.value = this.averageWage
    field.dispatchEvent(new Event("input", { bubbles: true }))
    field.focus()
  }

  crewRows() {
    return this.rowTargets
      .map((row) => ({
        wage_group_id: row.querySelector("[data-role='wage-group']")?.value,
        hours: row.querySelector("[data-role='hours']")?.value,
      }))
      .filter((row) => row.wage_group_id && row.hours)
  }

  // The Satz the panel writes into. An unsaved row is the one being added right now;
  // otherwise the newest persisted Satz, which renders first because the association is
  // ordered valid_from DESC. Never a row further down — those priced past Kalkulationen.
  targetRow() {
    const container = document.querySelector(this.ratesValue)
    if (!container) return null

    const rows = Array.from(container.querySelectorAll(".nested-fields")).filter((row) => {
      if (row.style.display === "none") return false
      const destroy = row.querySelector("input[name*='_destroy']")
      return !destroy || destroy.value !== "1"
    })

    if (rows.length === 0) return null

    return [...rows].reverse().find((row) => row.dataset.newRecord === "true") || rows[0]
  }

  // The ISO value of the row's Gültig-ab. The visible half of the date field shows the
  // German format; the hidden companion carries YYYY-MM-DD and is the one marked with the
  // data-role, so this is always the format the endpoint parses.
  validFromOf(row) {
    return row?.querySelector("[data-role='valid-from']")?.value || ""
  }

  showResult(payload) {
    this.errorTarget.hidden = true
    this.resultValueTarget.textContent = this.format(payload.average_wage, payload.total_hours)
    this.resultTarget.hidden = false
    this.applyTarget.hidden = false
  }

  showError(message) {
    this.hideResult()
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideResult() {
    this.resultTarget.hidden = true
    this.applyTarget.hidden = true
  }

  format(averageWage, totalHours) {
    const wage = Number(averageWage).toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
    })
    const hours = Number(totalHours).toLocaleString("de-DE")
    return `${wage} (${hours} h)`
  }
}
